import React, { useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Table, Tag, Typography, message } from "@mimber/ui-web"
import { useMemberGroups } from "@mimber/client/mimber-organization"
import { MemberGroupActionsDropdown } from "@/components/dropdowns"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { GroupMemberWithRelationsDto } from "@mimber/schema"
import type { MemberGroupsTableProps } from "./MemberGroupsTable.types"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
  getGroupTypeDisplay,
  getGroupVisibilityDisplay,
} from "@/utils"

const { Text } = Typography

export const MemberGroupsTable: React.FunctionComponent<
  MemberGroupsTableProps
> = ({ memberUuid, className, ...tableProps }) => {
  const navigate = useNavigate()
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "member-groups",
      defaultQueryParams: {
        page: 1,
        limit: 10,
      },
      scope: {
        memberUuid,
      },
    })

  const {
    data: response,
    isLoading,
    error,
  } = useMemberGroups(memberUuid, queryParams)

  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load member groups: ${error.message}`)
    }
  }, [error])

  const columns: ColumnsType<GroupMemberWithRelationsDto> = useMemo(
    () => [
      {
        title: "Group Name",
        dataIndex: ["group", "name"],
        key: "group.name",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        ...getColumnSearchProps("Search by group name"),
        render: (_, record) => <Text>{record.group?.name || "-"}</Text>,
      },
      {
        title: "Type",
        dataIndex: ["group", "type"],
        key: "group.type",
        render: (_, record) => {
          if (!record.group?.type) return <Text>-</Text>
          const { label, color } = getGroupTypeDisplay(record.group.type)
          return <Tag color={color}>{label}</Tag>
        },
        filters: [
          { text: "Committee", value: "COMMITTEE" },
          { text: "Chapter", value: "CHAPTER" },
          { text: "Interest Group", value: "INTEREST_GROUP" },
          { text: "Team", value: "TEAM" },
          { text: "Other", value: "OTHER" },
        ],
      },
      {
        title: "Visibility",
        dataIndex: ["group", "visibility"],
        key: "group.visibility",
        render: (_, record) => {
          if (!record.group?.visibility) return <Text>-</Text>
          const { label, color } = getGroupVisibilityDisplay(
            record.group.visibility
          )
          return <Tag color={color}>{label}</Tag>
        },
      },
      {
        title: "Role",
        dataIndex: ["groupRole", "name"],
        key: "groupRole.name",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (_, record) => <Text>{record.groupRole?.name || "-"}</Text>,
      },
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (title: string | null) => <Text>{title || "-"}</Text>,
      },
      {
        title: "Joined",
        dataIndex: "joinedAt",
        key: "joinedAt",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (joinedAt: string) => <Text>{formatTableDate(joinedAt)}</Text>,
      },
      {
        title: "Added",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (createdAt: string) => (
          <Text>{formatTableDate(createdAt)}</Text>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        fixed: "right",
        width: 80,
        render: (_, record) => (
          <div onClick={(event) => event.stopPropagation()}>
            <MemberGroupActionsDropdown
              member={record}
              memberUuid={memberUuid}
            />
          </div>
        ),
      },
    ],
    [memberUuid]
  )

  const onTableChange: TableProps<GroupMemberWithRelationsDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <>
      <Table<GroupMemberWithRelationsDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => {
            if (!record.group?.uuid) return
            navigate({
              to: "/members/groups-committees/$groupUuid",
              params: { groupUuid: record.group.uuid },
            })
          },
          style: { cursor: "pointer" },
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
